import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Table,
  Space,
  InputNumber,
  Divider,
  Row,
  Col,
  Typography,
  Radio,
  Tag,
  Alert,
  Upload,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  UploadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { Navigate } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import {
  createQuotation,
  getQuotationById,
  updateQuotation,
} from "../../api/quotationApi";
import { loadAllCustomer } from "../../redux/actions/customer/getCustomerAction";
import QuotationItemModal from "./QuotationItemModal";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";
import PoIngestDropzone from "../poIngest/PoIngestDropzone";
import PoIngestReviewModal from "../poIngest/PoIngestReviewModal";
import { linkPoIngestion, deletePoIngestion } from "../../api/poIngestApi";

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const QuotationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const isEditing = !!id;

  const customers = useSelector((state) => state.customers?.list);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [pricingType, setPricingType] = useState("individual"); // "individual" | "inner_combined" | "outer_combined"
  const [poDocUrl, setPoDocUrl] = useState(null);
  const [poFile, setPoFile] = useState(null);
  const [uploadingPo, setUploadingPo] = useState(false);
  const [ingestReviewOpen, setIngestReviewOpen] = useState(false);
  const [currentIngest, setCurrentIngest] = useState(null);
  const [pendingIngestionId, setPendingIngestionId] = useState(null);
  const [totals, setTotals] = useState({
    subtotal: 0,
    discount_amount: 0,
    tax_amount: 0,
    total_amount: 0,
  });

  // Load customers via Redux
  useEffect(() => {
    dispatch(loadAllCustomer({ page: 1, limit: 100, status: true }));
  }, [dispatch]);

  // Fetch quotation if editing
  useEffect(() => {
    if (isEditing) {
      const fetchQuotation = async () => {
        setLoading(true);
        try {
          const quotation = await getQuotationById(id);
          form.setFieldsValue({
            customer_id: quotation.customer_id,
            validity_days: quotation.validity_days,
            discount_percent: quotation.discount_percent || 0,
            tax_rate: quotation.tax_rate || 5,
            terms_conditions: quotation.terms_conditions,
            notes: quotation.notes,
            internal_notes: quotation.internal_notes,
            pricing_type: quotation.pricing_type || "individual",
            combined_rate: quotation.combined_rate || 0,
            inners_per_pack: quotation.inners_per_pack || 0,
            outers_per_pack: quotation.outers_per_pack || 0,
          });
          setPricingType(quotation.pricing_type || "individual");
          setItems(quotation.items || []);
          if (quotation.po_document_key) {
            try {
              const { data: poData } = await axios.get(`po-document/quotation/${quotation.id}/po-document`);
              setPoDocUrl(poData.url);
            } catch { setPoDocUrl(null); }
          } else {
            setPoDocUrl(null);
          }
        } catch (error) {
          toast.error("Failed to load quotation");
          console.log(error.message);
        } finally {
          setLoading(false);
        }
      };
      fetchQuotation();
    }
  }, [id, isEditing, form]);

  // Recalculate totals when items or form values change
  const recalculateTotals = useCallback(() => {
    const discount_percent = form.getFieldValue("discount_percent") || 0;
    const tax_rate = form.getFieldValue("tax_rate") || 5;
    const currentPricingType = form.getFieldValue("pricing_type") || pricingType;

    let subtotal = 0;
    if (currentPricingType === "inner_combined") {
      const combined_rate = Number(form.getFieldValue("combined_rate")) || 0;
      const inners = Number(form.getFieldValue("inners_per_pack")) || 0;
      subtotal = combined_rate * inners;
    } else if (currentPricingType === "outer_combined") {
      const combined_rate = Number(form.getFieldValue("combined_rate")) || 0;
      const outers = Number(form.getFieldValue("outers_per_pack")) || 0;
      subtotal = combined_rate * outers;
    } else {
      subtotal = items.reduce(
        (sum, item) => sum + (Number(item.total_amount) || 0),
        0
      );
    }

    const discount_amount = (subtotal * discount_percent) / 100;
    const taxable_amount = subtotal - discount_amount;
    const tax_amount = (taxable_amount * tax_rate) / 100;
    const total_amount = taxable_amount + tax_amount;

    setTotals({ subtotal, discount_amount, tax_amount, total_amount });
  }, [items, form, pricingType]);

  useEffect(() => {
    recalculateTotals();
  }, [items, recalculateTotals]);

  // Handle add/edit item from modal — itemData may be a single item or an array (group add)
  const handleItemSave = (itemData) => {
    const incoming = Array.isArray(itemData) ? itemData : [itemData];
    if (editingItemIndex !== null) {
      // Editing: replace only the item at that index (single item)
      const updatedItems = [...items];
      updatedItems[editingItemIndex] = incoming[0];
      setItems(updatedItems);
    } else {
      setItems((prev) => [...prev, ...incoming]);
    }
    setItemModalVisible(false);
    setEditingItemIndex(null);
  };

  // Handle delete item
  const handleDeleteItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  // Handle form submit
  const handleSubmit = async (values) => {
    if (items.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    // PO-ingested items skip the "Add Item" modal's own required-subcategory validation (they're
    // pushed straight into `items`), so it has to be enforced here instead — same requirement,
    // just checked at a different point.
    const missingSubcategoryIndex = items.findIndex((item) => !item.subcategory);
    if (missingSubcategoryIndex !== -1) {
      toast.error(
        `Item #${missingSubcategoryIndex + 1} (${items[missingSubcategoryIndex].item_name || "unnamed"
        }) needs a subcategory — edit it before saving`
      );
      return;
    }

    setLoading(true);
    try {
      const quotationData = {
        customer_id: values.customer_id,
        po_number: values.po_number || null,
        validity_days: values.validity_days,
        discount_percent: values.discount_percent || 0,
        tax_rate: values.tax_rate || 5,
        terms_conditions: values.terms_conditions,
        notes: values.notes,
        internal_notes: values.internal_notes,
        // Combined packing fields
        pricing_type: pricingType,
        combined_rate: ["inner_combined", "outer_combined"].includes(pricingType) ? Number(values.combined_rate) || 0 : null,
        inners_per_pack: ["inner_combined", "outer_combined"].includes(pricingType) ? Number(values.inners_per_pack) || 0 : null,
        outers_per_pack: ["inner_combined", "outer_combined"].includes(pricingType) ? Number(values.outers_per_pack) || 0 : null,
        items: items.map((item) => ({
          item_name: item.item_name,
          item_description: item.item_description,
          subcategory: item.subcategory,
          number_of_pieces: item.number_of_pieces || 1,
          length: Number(item.length),
          width: Number(item.width),
          height: Number(item.height),
          unit_type: item.unit_type,
          casting_type: item.casting_type,
          ply: Number(item.ply),
          layers:
            typeof item.layers === "string"
              ? item.layers
              : JSON.stringify(item.layers),
          is_printing: item.is_printing || false,
          printing_config:
            typeof item.printing_config === "string"
              ? item.printing_config
              : JSON.stringify(item.printing_config),
          is_coating: item.is_coating || false,
          coating_config:
            typeof item.coating_config === "string"
              ? item.coating_config
              : JSON.stringify(item.coating_config),
          joining_method: item.joining_method || null,
          roll_size: item.roll_size,
          cutting_size: item.cutting_size,
          net_gsm: item.net_gsm,
          top_gsm: item.top_gsm,
          box_weight: item.box_weight,
          quantity: Number(item.quantity),
          rate_per_unit: Number(item.rate_per_unit),
          total_amount: Number(item.total_amount),
          calculated_rate: Number(item.calculated_rate),
          top_conversion_rate: Number(item.top_conversion_rate),
          box_conversion_rate: Number(item.box_conversion_rate),
          sheets_per_box: item.sheets_per_box,
          boxes_per_sheet: item.boxes_per_sheet,
          material_cost_top: item.material_cost_top,
          material_cost_rest: item.material_cost_rest,
          print_cost: item.print_cost || 0,
          coating_cost_total: item.coating_cost_total || 0,
          coating_cost_lamination: item.coating_cost_lamination || 0,
          coating_cost_leafing: item.coating_cost_leafing || 0,
          coating_cost_spot_uv: item.coating_cost_spot_uv || 0,
          fixed_cost_total: item.fixed_cost_total || 0,
          fixed_cost_per_box: item.fixed_cost_per_box || 0,
          markup_percentage: item.markup_percentage || 0,
          discount_percentage: item.discount_percentage || 0,
          packing_role: item.packing_role || null,
          qty_per_pack: item.qty_per_pack || null,
          item_group_id: item.item_group_id || null,
          qty_per_group: item.qty_per_group || null,
          is_group_parent: item.is_group_parent || null,
          selected_machines:
            typeof item.selected_machines === "string"
              ? item.selected_machines
              : JSON.stringify(item.selected_machines),
          outsource_jobs:
            typeof item.outsource_jobs === "string"
              ? item.outsource_jobs
              : JSON.stringify(item.outsource_jobs),
        })),
      };

      if (isEditing) {
        await updateQuotation(id, quotationData);
        if (poFile) {
          await uploadPoDocument(id);
        }
        toast.success("Quotation updated successfully");
        navigate(`/quotation/${id}`);
      } else {
        const result = await createQuotation(quotationData);
        if (poFile && result.id) {
          await uploadPoDocument(result.id);
        }
        if (pendingIngestionId && result.id) {
          try {
            await linkPoIngestion(pendingIngestionId, { quotation_id: result.id });
          } catch (e) {
            toast.warning(
              "Quotation created but linking the source PO failed: " + (e.response?.data?.message || e.message)
            );
          }
        }
        toast.success(
          `Quotation ${result.quotation_number} created successfully`
        );
        navigate(`/quotation/${result.id}`);
      }
    } catch (error) {
      console.log(error.message);
      toast.error(
        isEditing
          ? "Failed to update quotation"
          : "Failed to create quotation"
      );
    } finally {
      setLoading(false);
    }
  };

  const uploadPoDocument = async (quotationId) => {
    try {
      setUploadingPo(true);
      const formData = new FormData();
      formData.append("po_document", poFile);
      const { data } = await axios.post(`po-document/quotation/${quotationId}/po-document`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      try {
        const { data: poData } = await axios.get(`po-document/quotation/${quotationId}/po-document`);
        setPoDocUrl(poData.url);
      } catch { setPoDocUrl(data.po_document_url); }
      setPoFile(null);
    } catch (error) {
      toast.error("Failed to upload PO document");
      console.error(error);
    } finally {
      setUploadingPo(false);
    }
  };

  const handleIngestConfirm = ({ header, items: ingestItems, ingestionId }) => {
    form.setFieldsValue({
      ...(header.customer_id && { customer_id: header.customer_id }),
      po_number: header.po_number || "",
      notes: header.notes || "",
    });
    setItems((prev) => [...prev, ...ingestItems]);
    setPendingIngestionId(ingestionId);
    setIngestReviewOpen(false);
  };

  // Discarding a draft that was never used to pre-fill the form — clean it up server-side so
  // it doesn't linger as a "ready" record the SHA-256 cache could later hand back.
  const handleIngestDiscard = async () => {
    setIngestReviewOpen(false);
    if (currentIngest?.id) {
      try {
        await deletePoIngestion(currentIngest.id);
      } catch (e) {
        console.log("Failed to discard PO ingestion draft:", e.message);
      }
    }
  };

  const handlePoRemove = async () => {
    if (!id) return;
    try {
      await axios.delete(`po-document/quotation/${id}/po-document`);
      setPoDocUrl(null);
      setPoFile(null);
      toast.success("PO document removed");
    } catch (error) {
      toast.error("Failed to remove PO document");
    }
  };

  // Auth check
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  const accessToken = localStorage.getItem("access-token");
  checkTokenExp(accessToken);

  // Item table columns
  const itemColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Item Name",
      dataIndex: "item_name",
      key: "item_name",
      ellipsis: true,
    },
    ...(pricingType === "inner_combined" || pricingType === "outer_combined"
      ? [
          {
            title: "Role",
            dataIndex: "packing_role",
            key: "packing_role",
            width: 100,
            render: (role) => (
              <Tag color={role === "inner" ? "blue" : "green"}>
                {role === "inner" ? "Inner" : "Outer"}
              </Tag>
            ),
          },
          {
            title: "Qty/Pack",
            dataIndex: "qty_per_pack",
            key: "qty_per_pack",
            align: "right",
            width: 90,
          },
        ]
      : []),
    {
      title: "Box Type",
      dataIndex: "subcategory",
      key: "subcategory",
    },
    {
      title: "Dimensions",
      key: "dimensions",
      render: (_, record) =>
        `${record.length} × ${record.width} × ${record.height} ${record.unit_type || ""}`,
    },
    {
      title: "Ply",
      dataIndex: "ply",
      key: "ply",
      render: (ply) => `${ply} Ply`,
      align: "center",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
    },
    ...(pricingType === "individual"
      ? [
          {
            title: "Rate/Unit",
            dataIndex: "rate_per_unit",
            key: "rate_per_unit",
            render: (rate) => `₹${Number(rate || 0).toFixed(2)}`,
            align: "right",
          },
          {
            title: "Total",
            dataIndex: "total_amount",
            key: "total_amount",
            render: (amount) =>
              `₹${Number(amount || 0).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
              })}`,
            align: "right",
          },
        ]
      : []),
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record, index) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingItemIndex(index);
              setItemModalVisible(true);
            }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteItem(index)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageTitle title="Back" />
      <Row className="mr-top" justify="space-between" gutter={[0, 30]}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} className="rounded column-design">
          <Card bordered={false}>
            <Title level={4} className="m-2 text-center">
              {isEditing ? "Edit Quotation" : "New Quotation"}
            </Title>

            {!isEditing && (
              <PoIngestDropzone
                onIngested={(draft, file) => {
                  setCurrentIngest(draft);
                  setIngestReviewOpen(true);
                  // Same PDF used for extraction becomes the quotation's attached PO document
                  // too — otherwise it's used once for OCR and then never referenced again.
                  if (file) setPoFile(file);
                }}
                customerIdHint={form.getFieldValue("customer_id")}
              />
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                validity_days: 30,
                discount_percent: 0,
                tax_rate: 5,
                pricing_type: "individual",
                combined_rate: 0,
                inners_per_pack: 0,
                outers_per_pack: 0,
              }}
            >
              {/* Customer & Basic Info */}
              <Card
                title="Basic Information"
                bordered={false}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="customer_id"
                      label="Customer"
                      rules={[
                        {
                          required: true,
                          message: "Please select a customer",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        placeholder="Select customer"
                        loading={!customers}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        filterSort={(optionA, optionB) =>
                          optionA?.children
                            .toLowerCase()
                            .localeCompare(optionB?.children.toLowerCase())
                        }
                      >
                        {customers &&
                          customers.map((cust) => (
                            <Option key={cust.id} value={cust.id}>
                              {cust.name}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item
                      name="po_number"
                      label="PO Number"
                    >
                      <Input placeholder="Customer PO #" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item label="PO Document">
                      {poDocUrl && !poFile ? (
                        <Space>
                          <a href={poDocUrl} target="_blank" rel="noopener noreferrer">
                            <Button icon={<FilePdfOutlined />} size="small">View</Button>
                          </a>
                          <Button size="small" danger onClick={handlePoRemove}>Remove</Button>
                        </Space>
                      ) : (
                        <Upload
                          beforeUpload={(file) => {
                            setPoFile(file);
                            return false;
                          }}
                          maxCount={1}
                          accept=".pdf,image/*"
                          fileList={poFile ? [{ uid: '-1', name: poFile.name, status: 'done' }] : []}
                          onRemove={() => setPoFile(null)}
                        >
                          <Button icon={<UploadOutlined />} size="small" loading={uploadingPo}>
                            {poDocUrl ? 'Replace' : 'Upload PO'}
                          </Button>
                        </Upload>
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item
                      name="validity_days"
                      label="Validity (Days)"
                      rules={[{ required: true }]}
                    >
                      <InputNumber
                        min={1}
                        max={90}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item name="discount_percent" label="Discount (%)">
                      <InputNumber
                        min={0}
                        max={100}
                        style={{ width: "100%" }}
                        onChange={() => {
                          // defer recalculation
                          setTimeout(recalculateTotals, 0);
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={4}>
                    <Form.Item name="tax_rate" label="Tax Rate (%)">
                      <InputNumber
                        min={0}
                        max={28}
                        style={{ width: "100%" }}
                        onChange={() => {
                          setTimeout(recalculateTotals, 0);
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Pricing Type */}
              <Card
                title="Pricing"
                bordered={false}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col xs={24}>
                    <Form.Item name="pricing_type" label="Rate Type">
                      <Radio.Group
                        onChange={(e) => {
                          setPricingType(e.target.value);
                          setTimeout(recalculateTotals, 0);
                        }}
                      >
                        <Radio value="individual">Individual Item Rates</Radio>
                        <Radio value="inner_combined">Inner-wise Combined Rate</Radio>
                        <Radio value="outer_combined">Outer-wise Combined Rate</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>

                {(pricingType === "inner_combined" || pricingType === "outer_combined") && (
                  <>
                    <Alert
                      message={pricingType === "inner_combined" ? "Inner-wise Combined Pricing" : "Outer-wise Combined Pricing"}
                      description={
                        pricingType === "inner_combined"
                          ? "Enter the rate per inner box. Subtotal = Rate × Inners/Pack."
                          : "Enter the rate per outer carton. Subtotal = Rate × Outers/Pack."
                      }
                      type="info"
                      showIcon
                      icon={<InfoCircleOutlined />}
                      style={{ marginBottom: 16 }}
                    />
                    <Row gutter={16}>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="combined_rate"
                          label={pricingType === "inner_combined" ? "Rate per Inner (₹)" : "Rate per Outer (₹)"}
                          rules={[{ required: true, message: "Enter rate" }]}
                        >
                          <InputNumber
                            min={0}
                            precision={2}
                            style={{ width: "100%" }}
                            placeholder="₹0.00"
                            onChange={() => setTimeout(recalculateTotals, 0)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="inners_per_pack"
                          label="No. of Inners / Pack"
                          rules={[{ required: true, message: "Enter inners count" }]}
                        >
                          <InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 4" onChange={() => setTimeout(recalculateTotals, 0)} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="outers_per_pack"
                          label="No. of Outers / Pack"
                          rules={[{ required: true, message: "Enter outers count" }]}
                        >
                          <InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 1" onChange={() => setTimeout(recalculateTotals, 0)} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
              </Card>

              {/* Line Items */}
              <Card
                title="Line Items"
                bordered={false}
                style={{ marginBottom: 16 }}
                extra={
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingItemIndex(null);
                      setItemModalVisible(true);
                    }}
                  >
                    Add Item
                  </Button>
                }
              >
                <Table
                  columns={itemColumns}
                  dataSource={items}
                  rowKey={(_, index) => index}
                  pagination={false}
                  scroll={{ x: 900 }}
                  locale={{
                    emptyText:
                      "No items added yet. Click 'Add Item' to configure a box and calculate its rate.",
                  }}
                />

                {/* Group Pricing Summary */}
                {items.some((i) => i.item_group_id) && (() => {
                  // Build a map of groups from items
                  const groupMap = {};
                  items.forEach((item) => {
                    if (!item.item_group_id) return;
                    if (!groupMap[item.item_group_id]) groupMap[item.item_group_id] = [];
                    groupMap[item.item_group_id].push(item);
                  });
                  return (
                    <div style={{ marginTop: 16 }}>
                      {Object.entries(groupMap).map(([gid, gItems]) => {
                        const parent = gItems.find((i) => i.is_group_parent);
                        const groupTotal = gItems.reduce(
                          (sum, i) => sum + (i.rate_per_unit || 0) * (i.qty_per_group || 1),
                          0
                        );
                        const parentQtyPerGroup = parent?.qty_per_group || 1;
                        return (
                          <Card
                            key={gid}
                            size="small"
                            title={
                              <span style={{ color: "#1677ff" }}>
                                Group: {parent?.item_name || "Item Group"} set
                              </span>
                            }
                            style={{ marginBottom: 8, borderColor: "#91caff", background: "#f0f9ff" }}
                          >
                            <Row gutter={[8, 4]}>
                              {gItems.map((item, i) => (
                                <Col key={i} xs={24}>
                                  <Row justify="space-between">
                                    <Col>
                                      {item.is_group_parent && <Tag color="blue">parent</Tag>}
                                      {item.qty_per_group}× {item.item_name}
                                      <span style={{ color: "#888", fontSize: 12 }}>
                                        {" "}@ ₹{Number(item.rate_per_unit || 0).toFixed(2)}
                                      </span>
                                    </Col>
                                    <Col>
                                      ₹{(Number(item.rate_per_unit || 0) * (item.qty_per_group || 1)).toFixed(2)}
                                    </Col>
                                  </Row>
                                </Col>
                              ))}
                              <Col xs={24}><Divider style={{ margin: "4px 0" }} /></Col>
                              <Col xs={24}>
                                <Row justify="space-between">
                                  <Col><strong>Group total (1 set)</strong></Col>
                                  <Col><strong>₹{groupTotal.toFixed(2)}</strong></Col>
                                </Row>
                              </Col>
                              {parent && (
                                <Col xs={24}>
                                  <Row justify="space-between">
                                    <Col>
                                      <span style={{ color: "#1677ff" }}>
                                        {parent.item_name}-wise price
                                        {parentQtyPerGroup > 1 && ` (÷ ${parentQtyPerGroup})`}
                                      </span>
                                    </Col>
                                    <Col>
                                      <strong style={{ fontSize: 16, color: "#1677ff" }}>
                                        ₹{(groupTotal / parentQtyPerGroup).toFixed(2)}
                                      </strong>
                                    </Col>
                                  </Row>
                                </Col>
                              )}
                              <Col xs={24}>
                                <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                                  All member-wise: {gItems.map((i) =>
                                    `${i.item_name}: ₹${(i.qty_per_group ? groupTotal / i.qty_per_group : 0).toFixed(2)}`
                                  ).join(" | ")}
                                </div>
                              </Col>
                            </Row>
                          </Card>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Totals */}
                {(items.length > 0 || pricingType === "inner_combined" || pricingType === "outer_combined") && (
                  <Row justify="end" style={{ marginTop: 24 }}>
                    <Col xs={24} md={8}>
                      <div
                        style={{
                          background: "#fafafa",
                          padding: 16,
                          borderRadius: 8,
                        }}
                      >
                        <Row justify="space-between" style={{ marginBottom: 8 }}>
                          <span>Subtotal:</span>
                          <strong>
                            ₹
                            {totals.subtotal.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </strong>
                        </Row>
                        <Row justify="space-between" style={{ marginBottom: 8 }}>
                          <span>
                            Discount (
                            {form.getFieldValue("discount_percent") || 0}%):
                          </span>
                          <span style={{ color: "#f5222d" }}>
                            - ₹
                            {totals.discount_amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </Row>
                        <Row justify="space-between" style={{ marginBottom: 8 }}>
                          <span>
                            Tax ({form.getFieldValue("tax_rate") || 5}%):
                          </span>
                          <span>
                            ₹
                            {totals.tax_amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </Row>
                        <Divider style={{ margin: "8px 0" }} />
                        <Row
                          justify="space-between"
                          style={{ fontSize: 16, fontWeight: "bold" }}
                        >
                          <span>Total:</span>
                          <span>
                            ₹
                            {totals.total_amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                )}
              </Card>

              {/* Notes & Terms */}
              <Card
                title="Notes & Terms"
                bordered={false}
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="notes"
                      label="Notes (visible to customer)"
                    >
                      <TextArea
                        rows={4}
                        placeholder="Add notes for the customer..."
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="terms_conditions"
                      label="Terms & Conditions"
                    >
                      <TextArea
                        rows={4}
                        placeholder="Payment terms, delivery conditions..."
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      name="internal_notes"
                      label="Internal Notes (not visible to customer)"
                    >
                      <TextArea
                        rows={4}
                        placeholder="Internal notes for your team..."
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Actions */}
              <Row justify="end" gutter={16}>
                <Col>
                  <Button onClick={() => navigate("/quotation")}>
                    Cancel
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    shape="round"
                  >
                    {isEditing ? "Update Quotation" : "Create Quotation"}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* Item Modal */}
      <QuotationItemModal
        visible={itemModalVisible}
        onCancel={() => {
          setItemModalVisible(false);
          setEditingItemIndex(null);
        }}
        onSave={handleItemSave}
        initialData={editingItemIndex !== null ? items[editingItemIndex] : null}
        pricingType={pricingType}
      />

      <PoIngestReviewModal
        open={ingestReviewOpen}
        ingestion={currentIngest}
        onConfirm={handleIngestConfirm}
        onDiscard={handleIngestDiscard}
        // Close has no "reopen later" affordance anywhere on this page — an unconfirmed draft
        // left behind here is exactly as orphaned as one explicitly discarded, so it gets the
        // same server-side cleanup.
        onClose={handleIngestDiscard}
        afterClose={() => setCurrentIngest(null)}
      />
    </>
  );
};

export default QuotationForm;
